/**
 * Where "la oferta original" actually is.
 *
 * A scraped offer carries two URLs and they are not interchangeable:
 *
 *   `application_link`  the posting itself — LinkedIn, Indeed, an ATS, the
 *                       employer's own careers page. What a person means by
 *                       "the original".
 *   `source_url`        where the scraper *found* it. For JSearch that is a
 *                       Google Jobs search page with a `#vhid=` fragment, which
 *                       does not reliably land on the offer at all.
 *
 * 112 of the 113 scraped offers in production have a `google.com/search?…`
 * `source_url`, and every one of them has a real `application_link`. So a screen
 * linking `source_url` sends you to Google every single time.
 *
 * This lives in `config/` rather than in a view because the rule already drifted
 * once: the review queue learned to prefer `application_link` and label the
 * Google URL honestly, and the prospect screen — a different file, the same
 * question — kept sending people to a search page. Two copies of "which link is
 * the real one" is one screen that is wrong about it.
 */

/**
 * Whether a URL is a search engine's results page rather than a destination.
 *
 * Deliberately narrow. It is not trying to classify the whole web — only to
 * recognise the one shape the scraper actually produces, so that a URL it does
 * not recognise is still offered rather than hidden.
 */
export function isSearchResultUrl(url: string | null | undefined): boolean {
    const text = (url ?? "").trim();
    if (!text) return false;
    try {
        const parsed = new URL(text);
        const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
        const isEngine = /^(google\.[a-z.]+|bing\.com|duckduckgo\.com|search\.yahoo\.com)$/.test(host);
        return isEngine && /^\/(search|jobs)?$/.test(parsed.pathname);
    } catch {
        return false;
    }
}

export interface OfferUrls {
    application_link?: string | null;
    source_url?: string | null;
}

/**
 * The link to put behind "Oferta original", or null when there is nothing
 * honest to point at.
 *
 * Returns null rather than falling back to a search page: a link labelled
 * "oferta original" that lands on Google results is worse than no link, because
 * it costs a click to discover it was never going to work.
 */
export function offerLink(offer: OfferUrls): string | null {
    const application = (offer.application_link ?? "").trim();
    if (application) return application;
    const source = (offer.source_url ?? "").trim();
    if (source && !isSearchResultUrl(source)) return source;
    return null;
}

/**
 * What to call the *other* link, when there is one worth offering.
 *
 * Kept because it is genuinely where the scraper found the offer, and named for
 * what it is rather than allowed to impersonate the posting.
 */
export function sourceLinkLabel(url: string | null | undefined): string {
    return isSearchResultUrl(url) ? "Buscar en Google" : "Fuente";
}
